import { ButcherHeader } from '@components/ButcherHeader';
import { SideNav } from '@components/OrderCreation/OrderSideNav';
import { OrderListBreadcrumbs } from '@components/OrderProcessing/OrderListBreadcrumbs';
import { useToast } from '@contexts/Toast';
import { faAngleLeft } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Axios } from '@helpers/ApiService';
import {
  AppSettingsSubscriptionSteps,
  errorMessage,
  OrderProcessingMasterData,
} from '@helpers/Constants';
import { Assets } from '@helpers/Constants/Assets';
import { ApiEndpoints } from '@helpers/Constants/Navigation';
import { PaymentHistory } from '@helpers/Models';
import {
  formatDateRange,
  handleDateFormat,
} from '@services/OrderCreationService';
import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export const BillingInvoiceDetails = () => {
  const navigate = useNavigate();
  const { showToastMessage } = useToast();
  const location = useLocation();
  const invoiceId = location.state.invoiceNumber;
  const [paymentHistory, setPaymentHistory] = useState<PaymentHistory[] | null>(
    null,
  );
  const [loading, setLoading] = useState<boolean>(false);

  const BreadCrumbsProps = {
    steps: AppSettingsSubscriptionSteps,
    activeItem: 'Invoice Details',
    type: OrderProcessingMasterData.APP_SETTINGS,
    invoiceName: invoiceId,
  };

  useEffect(() => {
    fetchPaymentHistory();
  }, []);

  /**
   * Fetches the user's payment history from the server.
   *
   * Behavior:
   * - Sends a request to retrieve payment history sorted by most recent entries.
   * - Optionally filters results based on the provided `invoiceId`.
   * - Updates the `paymentHistory` state with the fetched data.
   * - Manages loading state during the API call.
   * - Displays an error toast if the request fails.
   *
   * @async
   * @function fetchPaymentHistory
   * @returns {Promise<void>} Resolves when the operation is complete
   */
  const fetchPaymentHistory = async () => {
    try {
      setLoading(true);
      const response = await Axios.post(ApiEndpoints.FETCH_PAYMENT_HISTORY, {
        sort: 'mostRecent',
        search: invoiceId,
      });
      setPaymentHistory(response.data.data.paymentHistory);
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen gap-1">
      {/* Sidebar */}
      <SideNav active={10} />
      {/* Main Content */}
      <div
        className={`flex-1 p-1 overflow-auto relative ${loading ? 'opacity-50' : 'opacity-100'}`}
      >
        {/* Header */}
        <ButcherHeader />
        <OrderListBreadcrumbs orderInfo={BreadCrumbsProps} />
        {paymentHistory?.map((data) => (
          <>
            <div className="flex items-center justify-between mt-4 mb-4 p-2">
              <div className="flex items-center">
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className="text-[#1C1B1F] hover:text-primary text-lg mr-2 focus:outline-none"
                >
                  <FontAwesomeIcon icon={faAngleLeft} />
                </button>
                <h2 className="text-2xl font-semibold ml-2">#{invoiceId}</h2>
              </div>
              <div className="">
                <button
                  type="button"
                  className="flex flex-row gap-2"
                  onClick={() => window.open(data.invoicePdfUrl, '_blank')}
                >
                  <span className="mt-1">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M8 12L3 7L4.4 5.55L7 8.15V0H9V8.15L11.6 5.55L13 7L8 12ZM2 16C1.45 16 0.979167 15.8042 0.5875 15.4125C0.195833 15.0208 0 14.55 0 14V11H2V14H14V11H16V14C16 14.55 15.8042 15.0208 15.4125 15.4125C15.0208 15.8042 14.55 16 14 16H2Z"
                        fill="#004834"
                      />
                    </svg>
                  </span>
                  <span className="text-primary font-semibold">
                    Download PDF
                  </span>
                </button>
              </div>
            </div>

            {/* Main content */}

            <div className="p-2">
              {/* First card */}
              <div className="border border-[#E8E8E8] p-3 rounded-lg">
                {/* First row */}
                <div className="flex flex-row justify-between ">
                  <div className="flex flex-row gap-3">
                    <span>
                      <img
                        src={Assets.LOGO_ICON}
                        alt="Sidebar icon"
                        className="w-14 h-14 object-contain rounded-xl mb-1"
                      />
                    </span>
                    <span className="font-bold text-2xl mt-3">
                      {data.stripeInvoiceDetail.companyName}
                    </span>
                  </div>

                  <div className="font-bold text-3xl mt-3">
                    <span>#{data.invoiceId}</span>
                  </div>
                </div>
                {/* Second row */}
                <div className="flex flex-row justify-between">
                  <div className="text-sm text-[#6C757D] flex flex-col justify-between">
                    <span>{data.stripeInvoiceDetail.address}</span>
                    <span>{data.stripeInvoiceDetail.cityStateZip}</span>
                    <span>{data.stripeInvoiceDetail.email}</span>
                  </div>
                  <div>
                    <div className="inline-block mb-1 ml-2">
                      <div
                        className={`inline-flex items-center gap-1 text-lg font-semibold px-4 py-1 rounded-full w-fit ${
                          data.status === 1
                            ? 'bg-[#D1FAE5] text-[#10B981]'
                            : 'bg-[#F8D4D4] text-[#DC2626]'
                        }`}
                      >
                        <svg
                          width="10"
                          height="10"
                          viewBox="0 0 10 10"
                          fill="none"
                        >
                          <path
                            d="M0 5C0 2.23858 2.23858 0 5 0C7.76142 0 10 2.23858 10 5C10 7.76142 7.76142 10 5 10C2.23858 10 0 7.76142 0 5Z"
                            fill={data.status === 1 ? '#10B981' : '#DC2626'}
                          />
                        </svg>
                        <span>{data.status === 1 ? 'Paid' : 'Unpaid'}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Third row */}
                <div className="flex flex-row justify-between mt-5 font-medium">
                  <div className="flex flex-row justify-between gap-10">
                    <div className="flex flex-col gap-1">
                      <span className="uppercase text-[#6C757D] text-xs">
                        INVOICE CYCLE
                      </span>
                      <span className="text-[16px] text-[#1A1A1A]">
                        <span className="text-[16px] text-[#1A1A1A]">
                          {formatDateRange(
                            data.stripeInvoiceDetail.invoiceCycleStart,
                            data.stripeInvoiceDetail.invoiceCycleEnd,
                          )}
                        </span>
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="uppercase text-[#6C757D] text-xs">
                      INVOICE DATE
                    </span>
                    <span className="text-[16px] text-[#1A1A1A]">
                      {handleDateFormat(data.stripeInvoiceDetail.billingDate)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="mt-4 rounded-lg">
                <table className="w-full table-auto rounded-lg">
                  <thead className="bg-[#F8F9FA] sticky top-0 z-20">
                    <tr className="text-[#6C757D] text-sm">
                      <th className="px-4 py-2 text-start font-semibold">
                        DESCRIPTION
                      </th>
                      <th className="px-4 py-2 text-start font-semibold">
                        QUANTITY
                      </th>
                      <th className="px-4 py-2 text-center font-semibold">
                        RATE
                      </th>
                      <th className="px-4 py-2 text-end font-semibold">
                        AMOUNT
                      </th>
                    </tr>
                  </thead>
                  <tbody className="text-sm">
                    {/* Subscription row */}
                    <tr className="mt-3">
                      <td className="px-4 py-2 text-start">
                        <div className="flex flex-col">
                          <span className="font-medium">Subscription Fee</span>
                          <span className="text-[#6C757D]">
                            {data.stripeInvoiceDetail.description}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-2 text-start">
                        <span>{data.stripeInvoiceDetail.quantity}</span>
                      </td>
                      <td className="px-4 py-2 text-center">
                        <span>${data.stripeInvoiceDetail.rate.toFixed(2)}</span>
                      </td>
                      <td className="px-4 py-2 text-end font-bold text-[16px]">
                        <span>
                          $
                          {data.stripeInvoiceDetail.amountBeforeDiscount.toFixed(
                            2,
                          )}
                        </span>
                      </td>
                    </tr>

                    {/* Discount row */}
                    {data.stripeInvoiceDetail.discountAmount > 0 && (
                      <tr className="mt-3">
                        <td className="px-4 py-2 text-start">
                          <div className="flex flex-col">
                            <span className="font-medium">Discount</span>
                            <span className="text-[#6C757D]">
                              {data.stripeInvoiceDetail.discountDescription}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-2 text-start"></td>
                        <td className="px-4 py-2 text-center">
                          <span>
                            -$
                            {data.stripeInvoiceDetail.discountAmount.toFixed(2)}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-end font-bold text-[16px]">
                          <span className="text-[#10B981]">
                            -$
                            {data.stripeInvoiceDetail.discountAmount.toFixed(2)}
                          </span>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
                <div className="font-medium">
                  <div className="flex flex-row justify-between bg-[#F8F9FA] py-2 px-4">
                    <span>Subtotal</span>
                    <span>${data.stripeInvoiceDetail.subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex flex-row justify-between bg-[#F8F9FA] py-2 px-4">
                    <span>Discount</span>
                    <span className="text-[#10B981]">
                      -${data.stripeInvoiceDetail.subtotalDiscount.toFixed(2)}
                    </span>
                  </div>
                  {/* Tax display is commented out as it's not part of current integration */}
                  {/* <div className="flex flex-row justify-between bg-[#F8F9FA] py-2 px-4">
                    <span>Tax (8.5%)</span>
                    <span className="">$52.40</span>
                  </div> */}
                  <div className="flex flex-row justify-between bg-[#F8F9FA] font-bold py-2 px-4 text-2xl">
                    <span>Total Amount Due</span>
                    <span className="text-[#004834]">
                      ${data.stripeInvoiceDetail.totalAmountDue.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </>
        ))}
      </div>
    </div>
  );
};
